/**
 * m3 echo Frontend - Layers Tab
 *
 * Layer model configuration (moved from Settings): primary model and
 * fallback model per layer.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { settingsApi } from '../../services/api';
import type { LayerConfiguration } from '../../types/api';

type LayerKey = 'layer2' | 'layer2_1' | 'layer4' | 'layer5' | 'layer6';
type ModelField = `${LayerKey}_model`;
type FallbackField = `${LayerKey}_fallback_model`;
type ThinkingField = `${LayerKey}_thinking_level`;

const DEFAULT_CONFIGURATION: LayerConfiguration = {
  layer2_model: 'gemini-3.6-flash',
  layer2_fallback_model: 'gemini-3.8-flash',
  layer2_1_model: 'gemini-3.5-flash-lite',
  layer2_1_fallback_model: 'gemini-3.8-flash',
  layer4_model: 'gemini-3.6-flash',
  layer4_fallback_model: 'gemini-3.8-flash',
  layer5_model: 'gemini-3.1-pro-preview',
  layer5_fallback_model: 'gemini-3.8-flash',
  layer5_scraper_enabled: false,
  layer5_scraper_provider: 'firecrawl',
  layer6_model: 'gemini-3.6-flash',
  layer6_fallback_model: 'gemini-3.8-flash',
  insights_enabled: true,
  layer2_thinking_level: null,
  layer2_1_thinking_level: null,
  layer4_thinking_level: null,
  layer5_thinking_level: null,
  layer6_thinking_level: 'low',
};

// Value of the "Kein Fallback" option; stored as null.
const NO_FALLBACK = '';
// Value of the "Google-Standard" thinking option; stored as null.
const DEFAULT_LEVEL = '';

const LAYERS: { key: LayerKey; label: string }[] = [
  { key: 'layer2', label: 'Layer 2 Model (Response Analysis)' },
  { key: 'layer2_1', label: 'Layer 2.1 Model (Champion Consolidation)' },
  { key: 'layer4', label: 'Layer 4 Model (Insight Summarization)' },
  { key: 'layer5', label: 'Layer 5 Model (Optimierungsanalyse)' },
  { key: 'layer6', label: 'Layer 6 Model (Entwurf-Generierung)' },
];

export const LayersTab: React.FC = () => {
  const [config, setConfig] = useState<LayerConfiguration>(DEFAULT_CONFIGURATION);
  const [layerModels, setLayerModels] = useState<Record<LayerKey, string[]>>({
    layer2: [],
    layer2_1: [],
    layer4: [],
    layer5: [],
    layer6: [],
  });
  // Thinking levels each Google model takes (models without any are missing).
  const [thinkingLevels, setThinkingLevels] = useState<Record<string, string[]>>({});
  const [layerUpdateSuccess, setLayerUpdateSuccess] = useState(false);
  const [layerUpdateError, setLayerUpdateError] = useState<string | null>(null);

  useEffect(() => {
    loadModels();
    loadLayerConfiguration();
    loadThinkingLevels();
  }, []);

  const loadThinkingLevels = async () => {
    try {
      const response = await settingsApi.getThinkingLevels();
      setThinkingLevels(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Thinking-Level:', error);
    }
  };

  const loadModels = async () => {
    try {
      const response = await settingsApi.getModels();
      const modelsData = response.data;

      setLayerModels({
        layer2: modelsData.layer2 || [],  // Google models only: Layer 2 runs on Gemini
        layer2_1: modelsData.layer4 || [],
        layer4: modelsData.layer4 || [],
        layer5: modelsData.layer5 || [],  // Google grounding models only: Layer 5 uses Google Search
        layer6: modelsData.layer6 || [],  // Google grounding models only, like Layer 5
      });
    } catch (error) {
      console.error('Fehler beim Laden der Modelle:', error);
    }
  };

  const loadLayerConfiguration = async () => {
    try {
      const response = await settingsApi.getLayerConfiguration();
      const loaded = response.data;
      setConfig({
        ...loaded,
        layer6_model: loaded.layer6_model || DEFAULT_CONFIGURATION.layer6_model,
        layer5_scraper_enabled: loaded.layer5_scraper_enabled || false,
        layer5_scraper_provider: loaded.layer5_scraper_provider || 'firecrawl',
        insights_enabled: loaded.insights_enabled !== false,
      });
    } catch (error) {
      console.error('Fehler beim Laden der Layer-Konfiguration:', error);
    }
  };

  const update = (changes: Partial<LayerConfiguration>) => {
    setConfig((current) => ({ ...current, ...changes }));
  };

  const setPrimary = (key: LayerKey, model: string) => {
    const fallbackField: FallbackField = `${key}_fallback_model`;
    const levelField: ThinkingField = `${key}_thinking_level`;
    const level = config[levelField];
    // A fallback equal to the primary is pointless (and rejected by the API);
    // a thinking level the new model cannot take goes back to Google's default.
    update({
      [`${key}_model`]: model,
      ...(config[fallbackField] === model ? { [fallbackField]: null } : {}),
      ...(level && !(thinkingLevels[model] ?? []).includes(level) ? { [levelField]: null } : {}),
    });
  };

  const handleUpdateLayerConfiguration = async () => {
    setLayerUpdateError(null);
    try {
      await settingsApi.updateLayerConfiguration(config);
      setLayerUpdateSuccess(true);
      setTimeout(() => setLayerUpdateSuccess(false), 3000);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Layer-Konfiguration:', error);
      const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setLayerUpdateError(
        typeof detail === 'string' ? detail : 'Layer-Konfiguration konnte nicht gespeichert werden.'
      );
    }
  };

  const renderLayer = (key: LayerKey, label: string) => {
    const modelField: ModelField = `${key}_model`;
    const fallbackField: FallbackField = `${key}_fallback_model`;
    const levelField: ThinkingField = `${key}_thinking_level`;
    const primary = config[modelField];
    const options = layerModels[key];
    const levels = thinkingLevels[primary] ?? [];

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          width: { xs: '100%', sm: '70%' },
        }}
      >
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`${key}-model-label`}>{label}</InputLabel>
          <Select
            labelId={`${key}-model-label`}
            value={primary}
            label={label}
            onChange={(e) => setPrimary(key, e.target.value)}
          >
            {options.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`${key}-fallback-label`}>Fallback</InputLabel>
          <Select
            labelId={`${key}-fallback-label`}
            value={config[fallbackField] ?? NO_FALLBACK}
            label="Fallback"
            onChange={(e) => update({ [fallbackField]: e.target.value || null })}
          >
            <MenuItem value={NO_FALLBACK}>
              <em>Kein Fallback</em>
            </MenuItem>
            {options.map((model) => (
              <MenuItem key={model} value={model} disabled={model === primary}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 0.7 }} disabled={levels.length === 0}>
          <InputLabel id={`${key}-thinking-label`} shrink>Thinking</InputLabel>
          <Select
            labelId={`${key}-thinking-label`}
            value={config[levelField] ?? DEFAULT_LEVEL}
            label="Thinking"
            notched
            displayEmpty
            onChange={(e) => update({ [levelField]: e.target.value || null })}
          >
            <MenuItem value={DEFAULT_LEVEL}>
              <em>Google-Standard</em>
            </MenuItem>
            {levels.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Layer-Konfiguration
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Wählen Sie die KI-Modelle für Layer 2 (Response Analysis), Layer 4 (Insight Summarization), Layer 5 (Optimierungsanalyse) und Layer 6 (Entwurf-Generierung). Schlägt ein Aufruf mit dem primären Modell fehl, versucht der Layer es einmal mit seinem Fallback-Modell.
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Thinking: Google-Standard ist bei 3.6 Flash „medium“, bei 3.1 Pro „high“. Niedrigere Stufen sparen
        Output-Tokens und Zeit, können die Qualität aber verändern. Das Fallback-Modell bekommt die Stufe nur,
        wenn es sie unterstützt, sonst läuft es mit Google-Standard.
      </Typography>

      {layerUpdateSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Layer-Konfiguration erfolgreich aktualisiert!
        </Alert>
      )}

      {layerUpdateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLayerUpdateError(null)}>
          {layerUpdateError}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.insights_enabled}
              onChange={(e) => update({ insights_enabled: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Insights tool-weit aktivieren (Layer 3 + Layer 4)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Wenn deaktiviert, überspringen alle Jobs Layer 3 (Insight-Generierung) und der Layer 4 Endpoint (Zusammenfassung) liefert 503. Greift erst nach „Konfiguration speichern".
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start' }}
        />
        {!config.insights_enabled && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Insights sind deaktiviert. Neue Jobs werden ohne Layer-3-Daten ausgeführt.
          </Alert>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {LAYERS.map(({ key, label }) => (
          <React.Fragment key={key}>
            {renderLayer(key, label)}

            {key === 'layer5' && (
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: -1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.layer5_scraper_enabled}
                      onChange={(e) => update({ layer5_scraper_enabled: e.target.checked })}
                    />
                  }
                  label="Externer Scraper"
                />
                {config.layer5_scraper_enabled && (
                  <FormControl sx={{ minWidth: 160 }} size="small">
                    <InputLabel id="scraper-provider-label">Provider</InputLabel>
                    <Select
                      labelId="scraper-provider-label"
                      value={config.layer5_scraper_provider}
                      label="Provider"
                      onChange={(e) => update({ layer5_scraper_provider: e.target.value })}
                    >
                      <MenuItem value="firecrawl">Firecrawl</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </React.Fragment>
        ))}

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateLayerConfiguration}
          sx={{ alignSelf: 'flex-start' }}
        >
          Konfiguration speichern
        </Button>
      </Box>
    </Box>
  );
};

export default LayersTab;
